import { useServicesContext } from "@modules/core/services/services.context"
import { CustomerHistoryEntity } from "@modules/finances/entities/customerHistoryEntity"
import { type PageStripeType } from "@modules/finances/types/financesType"
import { useSuspenseQuery } from "@tanstack/react-query"
import { useEffect, useState } from "react"

export enum UseTransactionsHistoryKeys {
  BOX = "boxTransactionsHistory",
  USER = "userTransactionsHistory",
}

export const useTransactionsHistory = (apiKey: UseTransactionsHistoryKeys, stripeCustomerId: string) => {
  const { userFinanceApi, boxFinanceApi } = useServicesContext()

  const [pageSize, setPageSize] = useState(10)
  const [currentIndex, setCurrentIndex] = useState<number>(0)
  const [pageStripe, setPageStripe] = useState<PageStripeType[]>([
    {
      currentPage: undefined,
      previousPage: undefined,
      nextPage: undefined,
    },
  ])

  const financeApi = apiKey === UseTransactionsHistoryKeys.BOX ? boxFinanceApi : userFinanceApi

  const { data, isLoading } = useSuspenseQuery({
    queryKey: [apiKey, { stripeCustomerId, page: pageStripe[currentIndex]!.currentPage, size: pageSize }],
    queryFn: async () => {
      return await financeApi.getTransactionsHistory(stripeCustomerId, pageStripe[currentIndex]!.currentPage, pageSize)
    },
  })

  useEffect(() => {
    if (data.nextPage) {
      setPageStripe((prev) => {
        const updatedPageStripe = [...prev]
        updatedPageStripe[currentIndex] = {
          ...updatedPageStripe[currentIndex]!,
          nextPage: data.nextPage,
        }
        return updatedPageStripe
      })
    }
  }, [data.nextPage])

  const handlePreviousPage = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1)
    }
  }

  const handleNextPage = () => {
    if (pageStripe[currentIndex]!.nextPage) {
      setCurrentIndex((prev) => prev + 1)
      setPageStripe((prev) => {
        const updatedPageStripe = [...prev]
        updatedPageStripe.push({
          currentPage: pageStripe[currentIndex]!.nextPage,
          previousPage: pageStripe[currentIndex]!.currentPage,
          nextPage: undefined,
        })
        return updatedPageStripe
      })
    }
  }

  return {
    data: data.data.map((customerHistoryDto) => CustomerHistoryEntity.fromDto(customerHistoryDto)) || [],
    isLoading,
    hasMore: data.hasMore || false,
    pageSize,
    setPageSize,
    currentIndex,
    setCurrentIndex,
    handlePreviousPage,
    handleNextPage,
    hasPreviousPage: pageStripe[currentIndex]!.previousPage !== undefined || currentIndex > 0,
    hasNextPage: !!pageStripe[currentIndex]!.nextPage,
  }
}
